import { useState } from "react";
import styled from "styled-components";

import { useCompleteSetup, useSetup } from "../api/hooks";
import { Button, Card, ErrorNotice, Loading, SectionLabel } from "./primitives";

// In-app language picker shown when a game route returns `setup_incomplete`.
// Calls the SAME core setup pipeline as the bot (GET/POST .../setup), then the
// parent retries the gated flow via onDone.
const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space(6)};
`;

const Inner = styled.div`
  width: 100%;
  max-width: 360px;
`;

const Title = styled.h1`
  font-size: 20px;
  margin: ${({ theme }) => `${theme.space(2)} 0 ${theme.space(1)}`};
`;

const Sub = styled.p`
  color: ${({ theme }) => theme.color.textDim};
  font-size: 14px;
  margin: 0 0 ${({ theme }) => theme.space(4)};
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(2)};
  margin-bottom: ${({ theme }) => theme.space(4)};
`;

const Option = styled.button<{ $selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.space(3)} ${theme.space(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surfaceRaised};
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.color.accent : theme.color.border)};
  color: ${({ theme }) => theme.color.text};
  text-align: left;
  font-size: 15px;
`;

const Native = styled.span`
  color: ${({ theme }) => theme.color.text};
`;
const English = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.color.textDim};
`;

export function SetupPicker({ onDone }: { onDone: () => void }) {
  const { data, isLoading, error } = useSetup();
  const complete = useCompleteSetup();
  const [selected, setSelected] = useState<string | null>(null);

  if (isLoading) return <Loading label="loading setup…" />;
  if (error) return <ErrorNotice message={error.message} />;

  const languages = data?.languages ?? [];

  function submit() {
    if (!selected) return;
    complete.mutate(
      { language: selected },
      {
        onSuccess: (res) => {
          if (res.setupComplete) onDone();
        },
      }
    );
  }

  return (
    <Wrap>
      <Inner>
        <Card ticks>
          <SectionLabel>// setup</SectionLabel>
          <Title>Pick Rocky&rsquo;s language</Title>
          <Sub>Choose the language Rocky speaks in this chat to start playing.</Sub>
          <Options>
            {languages.map((lang) => (
              <Option
                key={lang.code}
                $selected={selected === lang.code}
                onClick={() => setSelected(lang.code)}
              >
                <Native>{lang.nativeName}</Native>
                <English>{lang.englishName}</English>
              </Option>
            ))}
          </Options>
          {complete.error && <ErrorNotice message={complete.error.message} />}
          <Button $full disabled={!selected || complete.isPending} onClick={submit}>
            {complete.isPending ? "saving…" : "Continue"}
          </Button>
        </Card>
      </Inner>
    </Wrap>
  );
}
